/**
 * MOCK · ASSUMED (CT-5715) — the Traits behind a profile's Intelligence tab.
 *
 * No backend contract exists yet: shapes follow the one assumed in CT-5715 and
 * the call sites carry `TODO(CT-5715 / backend)`. Real snake_case, real
 * envelope; only the transport is stubbed.
 *
 * The seed is the prototype's (record PER-0104, Normal scenario): 7 traits and 3
 * unverified ones, the same for every customer. Dates are RELATIVE to the moment
 * the seed is built, so "Expires in 13 days" and "Re-confirm due" stay true
 * whenever the app runs. Source lines name the record, read from the entity seed.
 *
 * The Insights (CT-5716) live here too: 5 insights and 1 emerging, as in the
 * prototype. CT-5714's How to approach reads the same seeds, so its counts match
 * the lists.
 *
 * QA switch, read from the URL on every request (`?mockIntelligence=…`), like the
 * `?mockFail=` of People & Access (CT-5612):
 *
 *   empty          no traits at all
 *   disconnected   Gmail is not connected; the traits still answer
 *   restricted     the viewer may not see any trait
 *   fail           the traits and insights requests fail
 *   report_fail    "Report an issue" fails
 *   sparse         not enough of a pattern yet: every insight is emerging
 *   paused         insights are paused (Gmail data missing)
 */

import { getMockEntityHeaders } from './entityHeader.mock.js';

const DAY_MS = 24 * 60 * 60 * 1000;

export const INTELLIGENCE_SCENARIOS = Object.freeze([
  'empty',
  'disconnected',
  'restricted',
  'fail',
  'report_fail',
  'sparse',
  'paused',
]);

const daysFromNow = (days) => new Date(Date.now() + days * DAY_MS).toISOString();

/** "Aug 28" — the form the prototype writes dates in inside a sentence. */
const shortDate = (iso) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

const SOURCES = {
  callNote: (author, at) => ({ kind: 'call_note', label: 'Call note', author, at }),
  email: (author, at) => ({ kind: 'email', label: 'Email', author, at }),
  meeting: (author, at) => ({ kind: 'meeting', label: 'Meeting', author, at }),
  call: (author, at) => ({ kind: 'call', label: 'Call', author, at }),
  privateNote: (author, at) => ({ kind: 'private_note', label: 'Private note', author, at }),
  calendar: (author, at) => ({ kind: 'calendar', label: 'Calendar invite', author, at }),
};

/**
 * One trait as the seed holds it. `evidenceDays` is how many days ago the latest
 * evidence arrived; `expiresInDays` is relative to today.
 */
const buildTraits = (name) => {
  const at = (daysAgo) => daysFromNow(-daysAgo);

  const traits = [
    {
      id: 't3',
      title: 'Technical Evaluator in the deal',
      category: 'role_in_deal',
      source_kind: 'noted',
      quote: null,
      evidence_at: at(47),
      expires_at: daysFromNow(13),
      status: 're_confirm_due',
      used_in: ['how_to_approach', 'next_best_action', 'drafts'],
      source_line: `Priya Nair's call notes, ${shortDate(at(47))}`,
      label: 'Role',
      value: 'Technical Evaluator',
      summary: `Priya Nair wrote this down after a call on ${shortDate(at(47))}. One source so far.`,
      source: SOURCES.callNote('Priya Nair', at(47)),
      visibility: 'everyone',
      history: [{ at: at(47), text: 'Extracted from a call note (Priya Nair)' }],
    },
    {
      id: 't1',
      title: 'Wants the migration timeline in writing',
      category: 'deal',
      source_kind: 'said',
      quote: 'Could you share the migration timeline in writing once it is confirmed?',
      evidence_at: at(57),
      expires_at: daysFromNow(3),
      status: 're_confirm_due',
      used_in: ['how_to_approach', 'next_best_action', 'drafts'],
      source_line: `From ${name}'s emails, ${shortDate(at(63))} and ${shortDate(at(57))}`,
      label: 'Request',
      value: 'Migration timeline, in writing',
      summary: `${name} asked for it twice by email. Two sources.`,
      source: SOURCES.email(name, at(57)),
      visibility: 'everyone',
      history: [
        { at: at(63), text: 'Extracted from an email' },
        { at: at(57), text: 'Confirmed by a second email' },
      ],
    },
    {
      id: 't7',
      title: 'Goes to Kansas City Chiefs home games',
      category: 'interests',
      source_kind: 'said',
      quote: "I'll be at Arrowhead on Sunday, as every Sunday.",
      evidence_at: at(50),
      expires_at: daysFromNow(10),
      status: 're_confirm_due',
      used_in: [],
      source_line: `From the ${shortDate(at(53))} QBR, noted by Priya Nair on ${shortDate(at(50))}`,
      label: 'Interest',
      value: 'Kansas City Chiefs home games',
      summary: `${name} said it during a QBR. One source so far.`,
      source: SOURCES.meeting('Priya Nair', at(50)),
      visibility: 'everyone',
      history: [{ at: at(50), text: 'Extracted from meeting notes (Priya Nair)' }],
    },
    {
      id: 't4',
      title: 'Signs emails "Sandy"',
      category: 'communication',
      source_kind: 'said',
      quote: 'Thanks — Sandy',
      evidence_at: at(57),
      expires_at: daysFromNow(125),
      status: 'active',
      used_in: ['drafts'],
      source_line: `From ${name}'s emails, signed "Sandy" since ${shortDate(at(57))}`,
      label: 'Name in email',
      value: 'Sandy',
      summary: 'Every email since then is signed the same way.',
      source: SOURCES.email(name, at(57)),
      visibility: 'everyone',
      history: [{ at: at(57), text: 'Extracted from an email signature' }],
    },
    {
      id: 't5',
      title: 'Wants "Sandra" in anything formal',
      category: 'communication',
      source_kind: 'noted',
      quote: 'Sandra asked us to keep "Sandra" in anything formal.',
      evidence_at: at(47),
      expires_at: daysFromNow(135),
      status: 'active',
      used_in: ['how_to_approach', 'drafts'],
      source_line: `Priya Nair's call notes, ${shortDate(at(47))}`,
      label: 'Name in proposals',
      value: 'Sandra',
      summary: `Priya Nair wrote this down after a call on ${shortDate(at(47))}. One source so far.`,
      source: SOURCES.callNote('Priya Nair', at(47)),
      visibility: 'everyone',
      history: [{ at: at(47), text: 'Extracted from a call note (Priya Nair)' }],
    },
    {
      id: 't6',
      title: 'Has daughters who play soccer',
      category: 'personal',
      source_kind: 'said',
      quote: "Sorry I'm late, I was dropping my daughters at soccer practice.",
      evidence_at: at(42),
      expires_at: daysFromNow(323),
      status: 'active',
      used_in: [],
      source_line: `From the ${shortDate(at(42))} call, at 0:40`,
      label: 'Family',
      value: 'Daughters who play soccer',
      summary: `${name} mentioned it at the start of a call.`,
      source: SOURCES.call(name, at(42)),
      visibility: 'everyone',
      history: [{ at: at(42), text: 'Extracted from a call recording' }],
    },
    {
      id: 't8',
      title: 'Wants a short agenda the day before a call',
      category: 'communication',
      source_kind: 'noted',
      quote: null,
      evidence_at: at(42),
      expires_at: daysFromNow(140),
      status: 'active',
      used_in: ['how_to_approach'],
      source_line: `Thomas González's private note, ${shortDate(at(42))}`,
      label: 'Meeting preference',
      value: 'A short agenda the day before',
      summary: 'Thomas González wrote this down in a private note.',
      source: SOURCES.privateNote('Thomas González', at(42)),
      visibility: 'private',
      history: [{ at: at(42), text: 'Extracted from a private note (Thomas González)' }],
    },
  ];

  const unverified = [
    {
      id: 'u1',
      title: 'Starting at a new company in January',
      category: 'role_in_deal',
      source_kind: 'noted',
      note: `Subject unclear. It may refer to ${name}'s manager.`,
      confidence: null,
      evidence_at: at(12),
      source_line: `From a forwarded email, ${shortDate(at(12))}`,
      disappears_in_days: 30,
      label: 'Job change',
      value: 'A new company in January',
      summary: 'One forwarded email. It is not clear who it is about.',
      source: SOURCES.email('Forwarded', at(12)),
      visibility: 'everyone',
      history: [{ at: at(12), text: 'Extracted from a forwarded email' }],
    },
    {
      id: 'u2',
      title: 'Has a recurring board meeting in Austin',
      category: 'personal',
      source_kind: 'noted',
      note: null,
      confidence: 0.41,
      evidence_at: at(15),
      source_line: `From calendar invite text, ${shortDate(at(15))}`,
      disappears_in_days: 30,
      label: 'Recurring meeting',
      value: 'Board meeting in Austin',
      summary: 'Read from the text of one calendar invite.',
      source: SOURCES.calendar(name, at(15)),
      visibility: 'everyone',
      history: [{ at: at(15), text: 'Extracted from a calendar invite' }],
    },
    {
      id: 'u3',
      title: 'Prefers calls on Fridays',
      category: 'communication',
      source_kind: 'said',
      note: null,
      confidence: 0.38,
      evidence_at: at(42),
      source_line: `From the ${shortDate(at(42))} call`,
      disappears_in_days: 30,
      label: 'Call preference',
      value: 'Fridays',
      summary: `${name} said something close to it once, in passing.`,
      source: SOURCES.call(name, at(42)),
      visibility: 'everyone',
      history: [{ at: at(42), text: 'Extracted from a call recording' }],
    },
  ];

  return { traits, unverified };
};

/** Reports opened through "Report an issue", keyed by record and trait. */
let openReports = new Set();
let scenarioOverride = null;

/** The record's name as the entity seed holds it, for the source lines. */
const nameOf = (entityType, entityId) => {
  const rows = getMockEntityHeaders()[entityType] ?? [];
  const row = rows.find((candidate) => (candidate.entity_id ?? candidate.entityId ?? candidate.id) === entityId);
  return row?.title ?? row?.name ?? 'this contact';
};

const readScenario = () => {
  if (scenarioOverride) return scenarioOverride;
  try {
    const value = new URLSearchParams(window.location.search).get('mockIntelligence');
    return INTELLIGENCE_SCENARIOS.includes(value) ? value : null;
  } catch {
    return null;
  }
};

export const getMockIntelligenceScenario = readScenario;

const reportKey = (entityType, entityId, traitId) => `${entityType}/${entityId}/${traitId}`;

const LIST_FIELDS = [
  'id',
  'title',
  'category',
  'source_kind',
  'quote',
  'source_line',
  'expires_at',
  'evidence_at',
  'status',
  'used_in',
];
const UNVERIFIED_FIELDS = ['id', 'title', 'category', 'source_kind', 'note', 'confidence', 'source_line', 'disappears_in_days'];

const pick = (row, fields) => Object.fromEntries(fields.filter((field) => field in row).map((field) => [field, row[field]]));

const matchesSearch = (row, search = {}) => {
  const needle = (search.query ?? '').trim().toLowerCase();
  if (!needle) return true;
  const fields = search.fields?.length ? search.fields : ['title'];
  return fields.some((field) => String(row[field] ?? '').toLowerCase().includes(needle));
};

/** `not_used` is the absence of every place a trait can be used. */
const matchesUsedIn = (row, values) =>
  values.some((value) => (value === 'not_used' ? row.used_in.length === 0 : row.used_in.includes(value)));

const matchesFilters = (row, filters = {}) => {
  const { source = [], status = [], used_in: usedIn = [] } = filters;
  if (source.length && !source.includes(row.source_kind)) return false;
  if (status.length && !status.includes(row.status)) return false;
  if (usedIn.length && !matchesUsedIn(row, usedIn)) return false;
  return true;
};

const byString = (field) => (a, b) => String(a[field] ?? '').localeCompare(String(b[field] ?? ''));

/** Each sort is a NAMED order; the direction only reverses it. */
const SORTS = {
  needs_attention: (a, b) =>
    Number(b.status === 're_confirm_due') - Number(a.status === 're_confirm_due') || byString('expires_at')(a, b),
  expires_soonest: byString('expires_at'),
  newest_evidence: (a, b) => byString('evidence_at')(b, a),
};

export const queryMockTraits = ({
  entityType,
  entityId,
  quickFilter = 'all',
  page = 1,
  pageSize = 10,
  orderBy = 'needs_attention',
  orderDirection = 'asc',
  filters,
  search,
}) => {
  const scenario = readScenario();
  if (scenario === 'fail') return { error: { status: 500, description: 'Mock traits failure (?mockIntelligence=fail).' } };

  const restricted = scenario === 'restricted';
  const { traits, unverified } =
    scenario === 'empty' || restricted ? { traits: [], unverified: [] } : buildTraits(nameOf(entityType, entityId));

  const inChip = (row) => quickFilter === 'all' || row.category === quickFilter;

  const compare = SORTS[orderBy] ?? SORTS.needs_attention;
  const sorted = traits
    .filter(inChip)
    .filter((row) => matchesSearch(row, search))
    .filter((row) => matchesFilters(row, filters))
    .sort(compare);
  if (orderDirection === 'desc') sorted.reverse();

  const unverifiedMatches = unverified.filter(inChip).filter((row) => matchesSearch(row, search));

  const start = (page - 1) * pageSize;
  const items = sorted.slice(start, start + pageSize).map((row) => pick(row, LIST_FIELDS));
  const totalPages = pageSize > 0 ? Math.max(1, Math.ceil(sorted.length / pageSize)) : 1;

  return {
    body: {
      items,
      unverified: {
        count: unverifiedMatches.length,
        items: unverifiedMatches.map((row) => pick(row, UNVERIFIED_FIELDS)),
      },
      source_status: { gmail: scenario === 'disconnected' ? 'disconnected' : 'connected' },
      restricted,
    },
    meta: {
      total_items: sorted.length,
      total_pages: totalPages,
      current_page: page,
      per_page: pageSize,
      has_next: start + items.length < sorted.length,
      has_previous: page > 1,
    },
  };
};

export const getMockTrait = ({ entityType, entityId, traitId }) => {
  const { traits, unverified } = buildTraits(nameOf(entityType, entityId));
  const trait = traits.find((row) => row.id === traitId);
  const candidate = trait ?? unverified.find((row) => row.id === traitId);
  if (!candidate) return { error: { status: 404, description: `No trait ${traitId}` } };

  return {
    body: {
      ...candidate,
      unverified: !trait,
      used_in: candidate.used_in ?? [],
      report_open: openReports.has(reportKey(entityType, entityId, traitId)),
    },
    meta: {},
  };
};

export const reportMockTrait = ({ entityType, entityId, traitId }) => {
  if (readScenario() === 'report_fail') {
    return { error: { status: 500, description: 'Mock report failure (?mockIntelligence=report_fail).' } };
  }
  const found = getMockTrait({ entityType, entityId, traitId });
  if (found.error) return found;
  openReports.add(reportKey(entityType, entityId, traitId));
  return { body: { trait_id: traitId, report_open: true }, meta: {} };
};

// ─── Insights (CT-5716) ──────────────────────────────────────────────────────

const EMERGING_NOTE = 'Below the 55% or 5-event threshold. Not used anywhere yet.';
const SPARSE_INTERACTIONS = 17;

/** `count` sources of an insight, newest first, each pointing at a History event. */
const buildSources = (insightId, count, at) => {
  const kinds = [
    { kind: 'email', label: 'Email', outcome: 'Email answered' },
    { kind: 'call', label: 'Call', outcome: 'Call reached them' },
    { kind: 'sms', label: 'Text', outcome: 'Text not answered' },
  ];
  return Array.from({ length: count }, (_, index) => {
    const kind = kinds[index % kinds.length];
    return {
      id: `${insightId}-s${index + 1}`,
      kind: kind.kind,
      label: kind.label,
      person: 'Priya Nair',
      // The newest source is known to the minute; older ones only by the day (a date, no time).
      occurred_at: index === 0 ? at(28) : at(28 + index * 9).slice(0, 10),
      outcome: kind.outcome,
      // An event that EXISTS on the Timeline (the 28 seeded messages, act-1 … act-28),
      // spread over its pages, so "Open in History" lands on something (CT-5724).
      history_event_id: `act-${((index * 7) % 28) + 1}`,
    };
  });
};

const buildInsights = (name) => {
  const at = (daysAgo) => daysFromNow(-daysAgo);

  const insights = [
    {
      id: 'i-channel',
      category: 'channel',
      title: 'Best channel',
      trend: 'weakening',
      value: 'Email',
      headline: 'Answered 7 of the last 8 · 88%',
      progress: 88,
      confidence: 0.88,
      source_line: 'Concierge · Last 120 days',
      evidence: [
        'Held 3 of the last 4; 4 of 4 before that',
        'Calls: 1 of 3 reached · Texts: 0 of 1 answered',
        `Said on the ${shortDate(at(42))} call: phone for anything urgent. Urgent outreach follows what ${name} said.`,
      ],
      has_attempts: true,
      changed_at: at(3),
      summary: 'Held 7 of the last 8 attempts over the last 120 days.',
      window: 'Last 120 days',
      sample: '8 attempts',
      sources: buildSources('i-channel', 12, at),
      recalculation_text: 'Recalculates with every new conversation',
      used_in: ['how_to_approach', 'drafts'],
      history: [{ at: at(100), text: 'Email' }],
    },
    {
      id: 'i-time',
      category: 'timing',
      title: 'Best time',
      trend: 'rising',
      value: 'Mornings',
      headline: '8 of the last 10 replies before noon · 80%',
      progress: 80,
      confidence: 0.8,
      source_line: 'Concierge · Last 120 days',
      evidence: ['Held 5 of the last 5; 3 of 5 before that', '8 of 10 replies sent before noon'],
      has_attempts: true,
      changed_at: at(6),
      summary: 'Most replies arrive before noon, and more so lately.',
      window: 'Last 120 days',
      sample: '10 replies',
      sources: buildSources('i-time', 10, at),
      recalculation_text: 'Recalculates with every new conversation',
      used_in: ['how_to_approach', 'drafts'],
      history: [{ at: at(90), text: 'Mornings' }],
    },
    {
      id: 'i-decide',
      category: 'how_they_decide',
      title: `How ${name} decides`,
      trend: 'stable',
      value: 'Cautious; wants detail in writing',
      headline: 'Consistent since August',
      progress: null,
      confidence: 0.75,
      source_line: 'Concierge · 17 conversations',
      evidence: [
        'Asked for the timeline in writing twice',
        'Asked for a data-residency note before committing',
      ],
      has_attempts: false,
      changed_at: at(15),
      summary: 'Asks for details in writing before moving forward.',
      window: 'Since August',
      sample: '17 conversations',
      sources: buildSources('i-decide', 4, at),
      recalculation_text: 'Recalculates weekly',
      used_in: ['how_to_approach', 'drafts'],
      history: [{ at: at(70), text: 'Cautious; wants detail in writing' }],
    },
    {
      id: 'i-renewal',
      category: 'deal_outlook',
      title: 'Renewal likelihood',
      trend: 'stable',
      value: 'High',
      headline: 'Up from Medium in September',
      progress: null,
      confidence: 0.7,
      source_line: 'Concierge · Refreshed weekly',
      evidence: ['Made two written requests', 'One question is still unanswered'],
      has_attempts: false,
      changed_at: at(20),
      summary: 'Engagement on the renewal went up in September.',
      window: 'Last 90 days',
      sample: '6 signals',
      sources: buildSources('i-renewal', 3, at),
      recalculation_text: 'Refreshed weekly',
      used_in: ['how_to_approach'],
      history: [
        { at: at(80), text: 'Medium' },
        { at: at(25), text: 'High' },
      ],
    },
    {
      id: 'i-scheduling',
      category: 'scheduling',
      title: 'Meeting scheduling',
      trend: 'low_data',
      value: 'Accepts 2+ days ahead',
      headline: 'Steady since July',
      progress: null,
      confidence: 0.6,
      source_line: 'Concierge · Last 90 days',
      evidence: ['Accepted 7 of 9 invites sent 2 or more days ahead', 'Declined both same-day invites'],
      has_attempts: false,
      changed_at: at(40),
      summary: 'Invites sent with notice are accepted; same-day ones are not.',
      window: 'Last 90 days',
      sample: '11 invites',
      sources: buildSources('i-scheduling', 3, at),
      recalculation_text: 'Recalculates weekly',
      used_in: ['how_to_approach'],
      history: [{ at: at(95), text: 'Accepts 2+ days ahead' }],
    },
  ];

  const emerging = [
    {
      id: 'i-reply',
      category: 'timing',
      title: 'Reply time',
      trend: 'emerging',
      value: 'About half within 2 days',
      headline: 'Answered 4 of the last 8 within 2 days · 50%',
      progress: 50,
      confidence: 0.5,
      source_line: 'Concierge · Last 120 days',
      evidence: ['Held 2 of the last 4; 2 of 4 before that', '4 of 8 emails answered within 2 days · median 1 day'],
      note: EMERGING_NOTE,
      has_attempts: true,
      changed_at: at(2),
      summary: 'Not a pattern yet: about half of the emails get an answer within two days.',
      window: 'Last 120 days',
      sample: '8 emails',
      sources: buildSources('i-reply', 8, at),
      recalculation_text: 'Recalculates with every new conversation',
      used_in: [],
      history: [{ at: at(2), text: 'About half within 2 days' }],
    },
  ];

  return { insights, emerging };
};

const INSIGHT_LIST_FIELDS = [
  'id',
  'category',
  'title',
  'trend',
  'value',
  'headline',
  'progress',
  'confidence',
  'source_line',
  'evidence',
  'has_attempts',
  'note',
  'paused',
];

/** Weakening and low-data insights are the ones to look at. */
const NEEDS_ATTENTION_TRENDS = ['weakening', 'low_data'];

const INSIGHT_SORTS = {
  recently_changed: (a, b) => byString('changed_at')(b, a),
  needs_attention: (a, b) =>
    Number(NEEDS_ATTENTION_TRENDS.includes(b.trend)) - Number(NEEDS_ATTENTION_TRENDS.includes(a.trend)) ||
    byString('changed_at')(b, a),
  most_confident: (a, b) => (b.confidence ?? 0) - (a.confidence ?? 0),
};

/** A paused insight keeps its identity and loses its numbers: nothing is read from it. */
const asPaused = (row) => ({
  ...row,
  trend: 'paused',
  headline: null,
  progress: null,
  confidence: null,
  evidence: [],
  note: 'Paused. Not used in How to approach.',
  paused: true,
});

/**
 * Below the threshold: the numbers that made it a pattern no longer hold. As in
 * the prototype, an insight measured as a share now reads "50%" (headline and
 * bar); one that was never a share keeps its headline and has no bar.
 */
const EMERGING_SHARE = 50;
const asEmerging = (row) => ({
  ...row,
  trend: 'emerging',
  headline: row.progress != null ? `${EMERGING_SHARE}%` : row.headline,
  progress: row.progress != null ? EMERGING_SHARE : null,
  confidence: row.progress != null ? EMERGING_SHARE / 100 : row.confidence,
  evidence: [],
  note: EMERGING_NOTE,
  used_in: [],
});

/**
 * What a QA scenario does to the seed, for the list AND the detail, so a card
 * and its slide-out always agree. As in the prototype, "all paused" pauses the
 * insights above the threshold and leaves the emerging one as it is.
 */
const applyInsightScenario = ({ insights, emerging }, scenario) => {
  if (scenario === 'sparse') return { insights: [], emerging: [...insights.map(asEmerging), ...emerging], paused: null };
  if (scenario === 'paused') {
    return { insights: insights.map(asPaused), emerging, paused: { since: daysFromNow(-8), source: 'gmail' } };
  }
  return { insights, emerging, paused: null };
};

export const queryMockInsights = ({
  entityType,
  entityId,
  quickFilter = 'all',
  orderBy = 'recently_changed',
  orderDirection = 'asc',
  filters,
  search,
}) => {
  const scenario = readScenario();
  if (scenario === 'fail') return { error: { status: 500, description: 'Mock insights failure (?mockIntelligence=fail).' } };

  const { insights, emerging, paused } = applyInsightScenario(buildInsights(nameOf(entityType, entityId)), scenario);

  const status = filters?.status ?? [];
  const inChip = (row) => quickFilter === 'all' || row.category === quickFilter;
  const inStatus = (row) => !status.length || status.includes(row.trend);

  const compare = INSIGHT_SORTS[orderBy] ?? INSIGHT_SORTS.recently_changed;
  const sorted = insights
    .filter(inChip)
    .filter((row) => matchesSearch(row, search))
    .filter(inStatus)
    .sort(compare);
  if (orderDirection === 'desc') sorted.reverse();

  const emergingMatches = emerging
    .filter(inChip)
    .filter((row) => matchesSearch(row, search))
    .filter(inStatus);

  return {
    body: {
      items: sorted.map((row) => pick(row, INSIGHT_LIST_FIELDS)),
      emerging: { count: emergingMatches.length, items: emergingMatches.map((row) => pick(row, INSIGHT_LIST_FIELDS)) },
      interactions_so_far: scenario === 'sparse' ? SPARSE_INTERACTIONS : null,
      paused,
    },
    meta: { total_items: sorted.length },
  };
};

export const getMockInsight = ({ entityType, entityId, insightId }) => {
  const { insights, emerging } = applyInsightScenario(buildInsights(nameOf(entityType, entityId)), readScenario());
  const insight = [...insights, ...emerging].find((row) => row.id === insightId);
  if (!insight) return { error: { status: 404, description: `No insight ${insightId}` } };

  return {
    body: {
      ...insight,
      visibility: 'everyone',
      sources_total: insight.sources.length,
      report_open: openReports.has(reportKey(entityType, entityId, insightId)),
    },
    meta: {},
  };
};

export const reportMockInsight = ({ entityType, entityId, insightId }) => {
  if (readScenario() === 'report_fail') {
    return { error: { status: 500, description: 'Mock report failure (?mockIntelligence=report_fail).' } };
  }
  const found = getMockInsight({ entityType, entityId, insightId });
  if (found.error) return found;
  openReports.add(reportKey(entityType, entityId, insightId));
  return { body: { insight_id: insightId, report_open: true }, meta: {} };
};

// ─── How to approach (CT-5714) ───────────────────────────────────────────────

/**
 * What the How to approach card is built from, read off the same seeds as the
 * lists so its counts always match them: the traits used in How to approach,
 * and the channel / timing insights behind its "Reach by" row.
 */
export const getMockApproachSources = ({ entityType, entityId }) => {
  const name = nameOf(entityType, entityId);
  const { traits } = buildTraits(name);
  const { insights } = buildInsights(name);
  return {
    traitIds: traits.filter((row) => row.used_in.includes('how_to_approach')).map((row) => row.id),
    reachByInsightIds: insights
      .filter((row) => ['channel', 'timing'].includes(row.category) && row.used_in.includes('how_to_approach'))
      .map((row) => row.id),
  };
};

/** Test-only: pin a QA scenario without touching the URL. */
export const __setMockIntelligenceScenario = (value) => {
  scenarioOverride = value;
};

/** Test-only: forget opened reports and any pinned scenario. */
export const __resetMockIntelligenceForTests = () => {
  openReports = new Set();
  scenarioOverride = null;
};
